import {
  Activity,
  Boxes,
  Bug,
  Globe,
  LayoutDashboard,
  LogOut,
  Map,
  MapPin,
  Network,
  Server,
  ShieldCheck,
  Smartphone,
  Terminal,
  type LucideIcon,
  Waypoints,
} from 'lucide-react';
import type { CSSProperties } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '@/auth/auth-provider';
import { Button } from '@/components/ui/button';
import { useFleetHealth } from '@/hooks/use-fleet-health';
import { cn } from '@/lib/utils';

/** Live fleet-API health, rendered as a signal dot + terse status word. */
function HealthPill(): JSX.Element {
  const { data, isError } = useFleetHealth();
  const degraded = isError || data?.status === 'degraded';

  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card/60 px-2.5 py-1">
      <span
        className={cn(
          'signal-dot',
          degraded ? 'bg-status-degraded' : 'animate-pulse-dot bg-status-active',
        )}
        style={
          {
            '--bg-dot': degraded ? 'var(--status-degraded)' : 'var(--status-active)',
          } as CSSProperties
        }
      />
      <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        Fleet {degraded ? 'degraded' : 'online'}
      </span>
    </div>
  );
}

function NavSection({ label }: { label: string }): JSX.Element {
  return (
    <p className="px-3 pb-1 pt-5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
      {label}
    </p>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium tracking-tight transition-colors',
          isActive
            ? 'bg-accent text-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Accent rail marks the active route. */}
          <span
            className={cn(
              'absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-primary transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
            aria-hidden
          />
          <Icon
            className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary' : 'text-muted-foreground')}
            aria-hidden
          />
          {label}
        </>
      )}
    </NavLink>
  );
}

export function AppLayout(): JSX.Element {
  const { user, role, canViewFleet, isAdmin, logout } = useAuth();

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 flex w-60 flex-col border-r border-border bg-card/40 backdrop-blur-sm">
        {/* Wordmark */}
        <div className="flex h-14 items-center gap-2.5 border-b border-border px-4">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/15 ring-1 ring-primary/30">
            <Server className="h-4 w-4 text-primary" aria-hidden />
          </span>
          <div className="leading-none">
            <p className="font-mono text-sm font-semibold uppercase tracking-[0.14em] text-foreground">
              Planet<span className="text-primary">·</span>Proxy
            </p>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">
              Fleet Console
            </p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {canViewFleet && (
            <>
              <NavSection label="Monitor" />
              <NavItem to="/overview" label="Overview" icon={LayoutDashboard} end />
              <NavItem to="/fleet/topology" label="Map" icon={Network} />

              <NavSection label="Fleet" />
              <NavItem to="/fleet" label="Servers" icon={Boxes} end />
              <NavItem to="/fleet/dashboard" label="Health" icon={Activity} />
              <NavItem to="/fleet/regions" label="Coverage" icon={Map} />
              <NavItem to="/fleet/playbooks" label="Playbooks" icon={Terminal} />

              <NavSection label="Analytics" />
              <NavItem to="/insights/regions" label="Regions" icon={MapPin} />
              <NavItem to="/insights/users" label="Users" icon={Globe} />
            </>
          )}

          {isAdmin && (
            <>
              <NavSection label="Edges" />
              <NavItem to="/bootstrap-pool" label="Bootstrap edges" icon={Waypoints} />

              <NavSection label="Support" />
              <NavItem to="/support/bug-reports" label="Bug reports" icon={Bug} />

              <NavSection label="Security" />
              <NavItem to="/security" label="Security" icon={ShieldCheck} />

              <NavSection label="Store" />
              <NavItem to="/store" label="Store Metadata" icon={Smartphone} />
            </>
          )}
        </nav>

        {/* Identity footer */}
        <div className="border-t border-border p-3">
          <div className="mb-2 flex items-center gap-2 px-1">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary font-mono text-xs uppercase text-muted-foreground">
              {String(user?.email ?? user?.id ?? '?').slice(0, 2)}
            </span>
            <div className="min-w-0 leading-tight">
              <p className="truncate text-xs font-medium text-foreground">
                {user?.email ?? user?.id ?? 'unknown'}
              </p>
              {role && (
                <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  {role}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="w-full justify-start text-muted-foreground"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col pl-60">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-md">
          <HealthPill />
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
